import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ExternalLink, TrendingUp, Award, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const NewslettersPage = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const newsletters = [
    {
      id: 1,
      title: "We Just Launched Something Big",
      subtitle: "Chef Compliance UK Has Landed",
      date: "June 2026",
      category: "Client Launch",
      featured: true,
      excerpt: "Chef Compliance UK is officially live and fully operational online — and we built every pixel of it. From brand design to content architecture, this wasn't just a website. This was positioning a business as an authority.",
      link: "https://chefcomplianceuk.com",
      deliverables: ["Brand Identity", "Full Website", "Booking System", "8+ Deliverables"],
      stats: {
        deliverables: "8+",
        code: "1k+",
        value: "∞"
      }
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Header onContactClick={scrollToContact} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-brand-brown/20 to-black"></div>
        <div className="absolute top-20 right-20 w-96 h-96 bg-brand-maroon/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-brand-olive/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-brand-maroon/20 rounded-full border border-brand-maroon/40 mb-6">
              <span className="text-brand-maroon font-bold">SUCCESS STORIES</span>
            </div>
            <h1 className="text-6xl lg:text-7xl font-bold text-white mb-6 drop-shadow-[0_0_30px_rgba(255,255,255,0.5)]">
              Our Client{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon via-brand-olive to-brand-maroon drop-shadow-[0_0_20px_rgba(139,35,50,0.8)]">
                Success Stories
              </span>
            </h1>
            <p className="text-2xl text-white leading-relaxed max-w-3xl mx-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
              Real results. Real businesses. Real growth. See how we transform digital presences into profit-generating machines.
            </p>
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient1)" />
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(139, 35, 50, 0.15)" />
              <stop offset="50%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="100%" stopColor="rgba(139, 35, 50, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Featured Newsletter */}
      <section className="py-20 px-6 bg-gradient-to-b from-brand-brown/10 to-black">
        <div className="container mx-auto max-w-6xl">
          {newsletters.map((newsletter) => (
            <div key={newsletter.id} className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl overflow-hidden shadow-2xl border-2 border-brand-maroon/50">
              {/* Featured badge */}
              {newsletter.featured && (
                <div className="bg-brand-maroon text-white py-3 px-6 text-center font-bold">
                  <Award className="w-5 h-5 inline-block mr-2" />
                  FEATURED CLIENT SUCCESS
                </div>
              )}
              
              <div className="p-8 md:p-12">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                  <div>
                    <div className="inline-block px-3 py-1 bg-brand-olive/20 rounded-full border border-brand-olive/40 text-brand-olive text-sm font-semibold mb-3">
                      {newsletter.category}
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold text-white mb-2 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
                      {newsletter.title}
                    </h2>
                    <p className="text-2xl text-brand-maroon font-semibold drop-shadow-[0_2px_8px_rgba(139,35,50,0.8)]">
                      {newsletter.subtitle}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-white/70">
                    <Calendar className="w-5 h-5" />
                    <span>{newsletter.date}</span>
                  </div>
                </div>

                {/* Excerpt */}
                <p className="text-lg text-white/90 leading-relaxed mb-8 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                  {newsletter.excerpt}
                </p>

                {/* Deliverables */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {newsletter.deliverables.map((item, index) => (
                    <div key={index} className="bg-black/40 backdrop-blur-sm rounded-xl p-4 border border-brand-olive/30 text-center">
                      <div className="text-brand-maroon font-bold mb-1">✓</div>
                      <div className="text-sm text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">{item}</div>
                    </div>
                  ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <TrendingUp className="w-8 h-8 text-brand-maroon mx-auto mb-2" />
                    <div className="text-3xl font-bold text-brand-maroon drop-shadow-[0_2px_8px_rgba(139,35,50,0.8)]">
                      {newsletter.stats.deliverables}
                    </div>
                    <div className="text-sm text-white/80">Deliverables</div>
                  </div>
                  <div className="text-center">
                    <Users className="w-8 h-8 text-brand-olive mx-auto mb-2" />
                    <div className="text-3xl font-bold text-brand-olive drop-shadow-[0_2px_8px_rgba(165,166,117,0.8)]">
                      {newsletter.stats.code}
                    </div>
                    <div className="text-sm text-white/80">Lines of Code</div>
                  </div>
                  <div className="text-center">
                    <Award className="w-8 h-8 text-brand-maroon mx-auto mb-2" />
                    <div className="text-3xl font-bold text-brand-maroon drop-shadow-[0_2px_8px_rgba(139,35,50,0.8)]">
                      {newsletter.stats.value}
                    </div>
                    <div className="text-sm text-white/80">Value Delivered</div>
                  </div>
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={newsletter.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-maroon hover:bg-brand-maroon/90 text-white font-bold rounded-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-brand-maroon/50"
                  >
                    Visit Chef Compliance UK
                    <ExternalLink className="w-5 h-5" />
                  </a>
                  <Button
                    onClick={scrollToContact}
                    variant="outline"
                    className="border-2 border-brand-maroon text-brand-maroon hover:bg-brand-maroon hover:text-white font-bold px-8 py-4"
                  >
                    Start Your Success Story
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24 rotate-180" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient2)" />
          <defs>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(139, 35, 50, 0.15)" />
              <stop offset="50%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="100%" stopColor="rgba(139, 35, 50, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <Footer />
    </div>
  );
};

export default NewslettersPage;
